import type { AnalysisBuffer } from '../types/image.js';
import type { BrightnessMetrics } from '../types/metrics.js';
import { clamp } from '../types/geometry.js';
import { mean, percentile } from '../geometry/statistics.js';

export const HISTOGRAM_BINS = 32;

/**
 * Exposure statistics.
 *
 * Clipping is counted at the extremes (>= 250 and <= 6) because a document
 * photo must not lose detail in the face: blown highlights on the forehead and
 * crushed shadows are both grounds for a retake.
 *
 * The same computation runs both on the whole frame and on the face region — a
 * deliberately white background is *supposed* to be near 255, so judging
 * clipping on the frame alone would flag correct photos.
 */
const brightnessFromValues = (values: ArrayLike<number>): BrightnessMetrics => {
  const histogram = new Array<number>(HISTOGRAM_BINS).fill(0);
  const count = values.length;
  if (count === 0) {
    return {
      mean: 0,
      median: 0,
      p05: 0,
      p95: 0,
      histogram,
      clippedHighlightsRatio: 0,
      clippedShadowsRatio: 0,
      dynamicRange: 0,
    };
  }

  const list: number[] = new Array(count);
  let sum = 0;
  let highlights = 0;
  let shadows = 0;
  for (let i = 0; i < count; i += 1) {
    const value = values[i] as number;
    list[i] = value;
    sum += value;
    if (value >= 250) highlights += 1;
    if (value <= 6) shadows += 1;
    const bin = Math.min(HISTOGRAM_BINS - 1, Math.floor((value / 256) * HISTOGRAM_BINS));
    histogram[bin] = (histogram[bin] as number) + 1;
  }

  const p05 = percentile(list, 0.05);
  const p95 = percentile(list, 0.95);

  return {
    mean: sum / count,
    median: percentile(list, 0.5),
    p05,
    p95,
    histogram: histogram.map((binCount) => binCount / count),
    clippedHighlightsRatio: highlights / count,
    clippedShadowsRatio: shadows / count,
    dynamicRange: p95 - p05,
  };
};

export const computeBrightness = (buffer: AnalysisBuffer): BrightnessMetrics =>
  brightnessFromValues(buffer.luma);

/**
 * Exposure statistics of a normalized region.
 *
 * `shape: 'ellipse'` inscribes the region in the face bounding box, which is a
 * good approximation of a face oval: the corners of a face box are always
 * background, and background is exactly where a white wall would be counted as
 * a blown highlight.
 */
export const computeBrightnessInRect = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  rect: { x: number; y: number; width: number; height: number },
  options: { shape?: 'rect' | 'ellipse' } = {},
): BrightnessMetrics => {
  const x0 = clamp(Math.floor(rect.x * width), 0, width - 1);
  const x1 = clamp(Math.ceil((rect.x + rect.width) * width), x0 + 1, width);
  const y0 = clamp(Math.floor(rect.y * height), 0, height - 1);
  const y1 = clamp(Math.ceil((rect.y + rect.height) * height), y0 + 1, height);

  const ellipse = options.shape === 'ellipse';
  const centreX = rect.x + rect.width / 2;
  const centreY = rect.y + rect.height / 2;
  const radiusX = Math.max(1e-6, rect.width / 2);
  const radiusY = Math.max(1e-6, rect.height / 2);

  const values: number[] = [];
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      if (ellipse) {
        const nx = (x + 0.5) / width;
        const ny = (y + 0.5) / height;
        if (((nx - centreX) / radiusX) ** 2 + ((ny - centreY) / radiusY) ** 2 > 1) continue;
      }
      values.push(luma[y * width + x] as number);
    }
  }
  return brightnessFromValues(values);
};

/** Mean luma of a normalized patch; used for the face-vs-background exposure checks. */
export const patchMean = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  rect: { x: number; y: number; width: number; height: number },
): number => {
  const values: number[] = [];
  const x0 = Math.max(0, Math.floor(rect.x * width));
  const x1 = Math.min(width, Math.ceil((rect.x + rect.width) * width));
  const y0 = Math.max(0, Math.floor(rect.y * height));
  const y1 = Math.min(height, Math.ceil((rect.y + rect.height) * height));
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      values.push(luma[y * width + x] as number);
    }
  }
  return mean(values);
};
